// A client who is BOTH selling and buying gets two files — and used to be
// asked to fill "All About You" out twice, once per portal. This carries their
// existing All About You answers from any other file they're on into a file
// whose portal is being issued, so the second set of forms shows up pre-filled.
//
// Scope is deliberately just All About You: it's the personal, per-contact
// form (instance id "allAboutYou:{contactId}" on BOTH sides). Every other
// form is about the property or the side (utility sheet, buyer preferences),
// where carrying answers across files would be wrong.
//
// Answers are copied WITHOUT submittedAt — the client reviews what's there
// and submits on the new file; nothing is submitted on their behalf. An
// entry the target file already has (submitted or in progress) is never
// touched.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";

const hasRealAnswer = (answers) =>
  !!answers &&
  Object.values(answers).some((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return !!(v.name || v.value);
    return String(v).trim() !== "";
  });

// Pure: given the target's portalForms, the file's contact ids, and the other
// deals the contact appears on, pick which allAboutYou entries to copy in.
// Returns { [instanceId]: entry } — empty object when there's nothing to do.
export function pickCarryoverEntries(
  targetPortalForms = {},
  contactIds = [],
  otherDeals = [],
) {
  const out = {};
  for (const cid of contactIds) {
    const id = `allAboutYou:${String(cid || "").trim()}`;
    if (!String(cid || "").trim()) continue;

    // The target already has something for this client — leave it alone.
    const existing = targetPortalForms[id];
    if (existing && (existing.submittedAt || hasRealAnswer(existing.answers)))
      continue;

    // Best source: latest submitted entry; otherwise the in-progress entry
    // with the most answered fields.
    let best = null;
    for (const d of otherDeals) {
      const entry = (d && d.portalForms && d.portalForms[id]) || null;
      if (!entry || !hasRealAnswer(entry.answers)) continue;
      const cand = {
        answers: entry.answers,
        submittedAt: entry.submittedAt || 0,
        filled: Object.keys(entry.answers || {}).length,
        fromDealId: String(d.id || d.createdat || ""),
      };
      if (
        !best ||
        cand.submittedAt > best.submittedAt ||
        (cand.submittedAt === best.submittedAt && cand.filled > best.filled)
      ) {
        best = cand;
      }
    }
    if (best) {
      out[id] = {
        answers: best.answers,
        carriedFrom: best.fromDealId,
        carriedAt: Date.now(),
      };
    }
  }
  return out;
}

// Contact ids on a deal: modern deals store sync_contacts id strings in
// `customers`; some paths use objects; the portal seed also uses `contactId`.
function dealContactIds(deal) {
  const ids = [];
  const push = (v) => {
    const id = String(v || "").trim();
    if (id && !ids.includes(id)) ids.push(id);
  };
  push(deal?.contactId);
  (Array.isArray(deal?.customers) ? deal.customers : []).forEach((c) =>
    push(typeof c === "string" ? c : c && (c.contactId || c.id)),
  );
  (deal?.coClients || []).forEach((c) => push(c && (c.contactId || c.id)));
  return ids;
}

// Best-effort: find the client's other files, copy their All About You
// answers onto this file where it has none. Resolves { copied: [ids] } and
// NEVER throws — a carryover hiccup must not break issuing the portal link.
export async function carryOverPortalAnswers(dealId) {
  try {
    const id = String(dealId || "");
    if (!id) return { copied: [] };
    const snap = await getDoc(doc(firestored, "deals", id));
    if (!snap.exists()) return { copied: [] };
    const target = { id, ...snap.data() };
    const contactIds = dealContactIds(target).slice(0, 6);
    if (!contactIds.length) return { copied: [] };

    // One query per contact (array-contains allows a single value). Modern
    // deals store contact ids as strings, so this matches those; legacy
    // object-customer deals predate the portal and have nothing to carry.
    const others = new Map();
    for (const cid of contactIds) {
      const q = query(
        collection(firestored, "deals"),
        where("customers", "array-contains", cid),
      );
      const res = await getDocs(q);
      res.forEach((d) => {
        if (d.id !== id && !others.has(d.id))
          others.set(d.id, { id: d.id, ...d.data() });
      });
    }

    const entries = pickCarryoverEntries(
      target.portalForms || {},
      contactIds,
      Array.from(others.values()),
    );
    const copied = Object.keys(entries);
    if (copied.length) {
      await setDoc(
        doc(firestored, "deals", id),
        { portalForms: entries },
        { merge: true },
      );
    }
    return { copied };
  } catch (e) {
    console.error("Portal answer carryover failed (portal still works):", e);
    return { copied: [], error: e?.message };
  }
}
